import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";
import styles from "./Badge.module.scss";
import { SrOnly } from "../SrOnly";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { LayoutUtilProps } from "../../types";

/**
 * Props for the Badge component
 * @extends ComponentPropsWithoutRef<"sup">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 */
export type BadgeProps = ComponentPropsWithoutRef<"sup"> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> & {
    /**
     * Optional offset configuration for badge positioning
     */
    offset?: {
      /**
       * Horizontal offset from the default position
       * @default "0px"
       */
      x?: string;
      /**
       * Vertical offset from the default position
       * @default "0px"
       */
      y?: string;
    };
  };

/**
 * Badge component for displaying small status indicators or counts.
 *
 * Features:
 * - Positioned as a superscript element
 * - Customizable offset positioning
 * - Screen reader support with aria-label
 * - Empty state styling when no content
 * - Supports layout utilities for positioning
 * - Accessible with proper ARIA roles
 *
 * @example
 * <Button>
 *   Notifications
 *   <Badge aria-label="3 new messages">3</Badge>
 * </Button>
 */
export const Badge = forwardRef<HTMLScriptElement, BadgeProps>((props, ref) => {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    offset,
    children,
    style,
    className,
    "aria-label": ariaLabel,
    ...rest
  } = componentProps;
  const classNames = cx(styles["badge"], className, {
    [styles.empty]: !children,
  });
  const offsetX = offset?.x ?? `0px`;
  const offsetY = offset?.y ?? `0px`;
  const iStyle = {
    ...style,
    ...layoutStyles,
    "--offsetY": offsetY,
    "--offsetX": offsetX,
  };

  return (
    <sup
      className={classNames}
      style={iStyle}
      data-anv="badge"
      {...rest}
      ref={ref}
    >
      <SrOnly>{ariaLabel}</SrOnly>
      {children}
    </sup>
  );
});
Badge.displayName = "Badge";
